import { useEffect, useState } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute } from "@tanstack/react-router";
import { TableSkeleton } from "@/components/loading-skeletons";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Tag, Inbox } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/payment-types")({
  head: () => ({ meta: [{ title: "Jenis Pembayaran — MI Al-Munajah Admin" }] }),
  component: PaymentTypesPage,
});

type PaymentType = {
  id: string;
  name: string;
  description: string | null;
  default_amount: number;
  is_active: boolean;
  created_at: string;
};

const emptyForm = { name: "", description: "", default_amount: 0, is_active: true };

function PaymentTypesPage() {
  const [rows, setRows] = useCachedState<PaymentType[]>("payment-types:rows", []);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PaymentType | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [delId, setDelId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("payment_types")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error("Gagal memuat: " + error.message);
    else setRows((data ?? []) as PaymentType[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (r: PaymentType) => {
    setEditing(r);
    setForm({
      name: r.name,
      description: r.description ?? "",
      default_amount: Number(r.default_amount),
      is_active: r.is_active,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama wajib diisi");
    if (form.default_amount < 0) return toast.error("Nominal tidak boleh negatif");
    setBusy(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      default_amount: Number(form.default_amount) || 0,
      is_active: form.is_active,
    };
    const { error } = editing
      ? await supabase.from("payment_types").update(payload).eq("id", editing.id)
      : await supabase.from("payment_types").insert(payload);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(editing ? "Jenis pembayaran diperbarui" : "Jenis pembayaran dibuat");
    setOpen(false);
    load();
  };

  const remove = async () => {
    if (!delId) return;
    setBusy(true);
    const { error } = await supabase.from("payment_types").delete().eq("id", delId);
    setBusy(false);
    setDelId(null);
    if (error) return toast.error("Gagal menghapus: " + error.message);
    toast.success("Jenis pembayaran dihapus");
    load();
  };

  const toggleActive = async (r: PaymentType) => {
    const { error } = await supabase
      .from("payment_types")
      .update({ is_active: !r.is_active })
      .eq("id", r.id);
    if (error) return toast.error(error.message);
    load();
  };

  return (
    <div>
      <PageHeader
        title="Jenis Pembayaran"
        description="Kelola katalog jenis tagihan: SPP, seragam, daftar ulang, uang gedung, dan lainnya."
        actions={
          <Button onClick={openCreate}><Plus className="w-4 h-4" /> Tambah Jenis</Button>
        }
      />

      <Card className="p-4 rounded-2xl">
        <div className="rounded-xl border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nama</TableHead>
                <TableHead>Deskripsi</TableHead>
                <TableHead className="text-right">Nominal Default</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableSkeleton columns={5} />
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-12">
                    <div className="flex flex-col items-center text-muted-foreground gap-2">
                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                        <Inbox className="w-6 h-6" />
                      </div>
                      <div className="font-medium text-foreground">Belum ada jenis pembayaran</div>
                      <div className="text-xs">Tambahkan jenis tagihan pertama untuk mulai membuat tagihan siswa.</div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <Tag className="w-4 h-4" />
                    </div>
                    {r.name}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm max-w-md truncate">
                    {r.description || "—"}
                  </TableCell>
                  <TableCell className="text-right font-semibold">{formatIDR(r.default_amount)}</TableCell>
                  <TableCell>
                    <button onClick={() => toggleActive(r)} className="cursor-pointer">
                      {r.is_active ? (
                        <Badge variant="outline" className="bg-success/15 text-success border-success/30">Aktif</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-muted text-muted-foreground border-border">Nonaktif</Badge>
                      )}
                    </button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(r)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setDelId(r.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Jenis Pembayaran" : "Tambah Jenis Pembayaran"}</DialogTitle>
            <DialogDescription>Master data jenis tagihan yang dapat dipakai berulang.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama <span className="text-destructive">*</span></Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Contoh: SPP Bulanan"
              />
            </div>
            <div>
              <Label>Deskripsi</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Penjelasan singkat (opsional)"
                rows={3}
              />
            </div>
            <div>
              <Label>Nominal Default (Rp)</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={form.default_amount ? form.default_amount.toLocaleString("id-ID") : ""}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "");
                  setForm({ ...form, default_amount: digits ? Number(digits) : 0 });
                }}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground mt-1">Bisa di-override saat membuat tagihan.</p>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <div className="text-sm font-medium">Aktif</div>
                <div className="text-xs text-muted-foreground">Hanya jenis aktif yang muncul saat membuat tagihan.</div>
              </div>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={busy}>{editing ? "Simpan" : "Tambah"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus jenis pembayaran?</AlertDialogTitle>
            <AlertDialogDescription>
              Jenis pembayaran yang sudah dipakai tagihan tidak dapat dihapus. Nonaktifkan saja jika ingin menyembunyikan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function formatIDR(n: number) {
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(Number(n) || 0);
}
